import { useState } from 'react';

import { useCheckout } from '@/hooks/useCheckout';
import { useRouter } from '@/hooks/useRouter';
import {
  generateEyewearDetailPagePath,
  generateStrapDetailPagePath,
  generateWatchDetailPagePath,
  isEyewearHit,
  isStrapHit,
  isWatchHit,
  sendAlgoliaEvent as originSendAlgoliaEvent,
} from '@/integrations/algolia/helpers';
import type { ProductHit, WatchHit } from '@/integrations/algolia/types';
import { AlgoliaEventsNames } from '@/integrations/algolia/types';
import { useAlgoliaEvent } from '@/providers/AlgoliaEventProvider';
import { useCommonModalProviderContext } from '@/providers/CommonModalProvider';
import { useRegionProvider } from '@/providers/RegionProvider';

export const useProductListCardCommon = (hit: ProductHit) => {
  const { push } = useRouter();
  const { openNotifyMe, setContactUsModalStatus } =
    useCommonModalProviderContext();
  const [isLocalProcessing, setIsLocalProcessing] = useState(false);

  const { indexName, isSearched, objectIDs, queryID, setObjectIDs } =
    useAlgoliaEvent();

  const {
    endProcessing,
    lineAdd,
    snapshot: { isProcessing },
    startProcessing,
  } = useCheckout();

  const {
    locale,
    productCatalogMode: contextProductCatalogMode,
    query,
  } = useRegionProvider();
  const name = (
    hit.name[locale?.code?.toUpperCase() || 'EN_GB'] ||
    hit.name['EN_GB'] ||
    hit.name['EN_US']
  ).replace(/\u00A0/g, ' ');

  const detailPath = generateDetailPagePath(hit);

  const sendAlgoliaEvent = () => {
    if (isSearched) {
      originSendAlgoliaEvent({
        event: AlgoliaEventsNames.addedToCartObjectIDsAfterSearch,
        objectIDs: [hit.objectID],
        queryID: queryID,
        indexName: indexName,
        value: hit.price,
        currency: hit.currency,
      });
      setObjectIDs([...objectIDs, hit.objectID]);
    }
  };
  const imageUrl = (hit as WatchHit)?.image_url ?? hit?.thumbnail_url;

  const handleClickEvent = async (
    e: React.MouseEvent,
    customOrderFun?: () => Promise<void>,
    productCatalogMode = contextProductCatalogMode,
  ) => {
    e.preventDefault();
    e.stopPropagation();

    if (productCatalogMode) {
      return push(detailPath);
    } else if (
      hit.call_to_action === 'ORDER' ||
      hit.call_to_action === 'PRE_ORDER'
    ) {
      if (customOrderFun) {
        await customOrderFun();
      } else {
        setIsLocalProcessing(true);
        try {
          await lineAdd(hit?.variant_id, hit?.call_to_action);
        } catch (error) {
          console.error(error);
        } finally {
          setIsLocalProcessing(false);
        }
      }
    } else if (hit.call_to_action === 'NOTIFY') {
      if (hit.objectID) {
        openNotifyMe({ sku: hit.objectID });
      }
    } else if (hit.call_to_action === 'NO_CTA') {
      setContactUsModalStatus(true, hit.objectID);
    }

    sendAlgoliaEvent();
  };
  return {
    locale,
    query,
    detailPath,
    openNotifyMe,
    setContactUsModalStatus,
    isLocalProcessing,
    setIsLocalProcessing,
    sendAlgoliaEvent,
    name,
    contextProductCatalogMode,
    imageUrl,
    handleClickEvent,
    endProcessing,
    lineAdd,
    isProcessing,
    startProcessing,
  };
};

export const generateDetailPagePath = (hit: ProductHit) => {
  if (isWatchHit(hit)) {
    return generateWatchDetailPagePath(hit);
  } else if (isStrapHit(hit)) {
    return generateStrapDetailPagePath(hit);
  } else if (isEyewearHit(hit)) {
    return generateEyewearDetailPagePath(hit);
  } else {
    return '#';
  }
};
